import * as types from './ActionTypes';
import axios from 'axios';
import { gcApi } from 'config/axios';

// Fetches
export const fetchViewerUserPreferences = (evictCache = false) => {
    return {
        type: types.FETCH_VIEWER_USER_PREFERENCES,
        meta: {
            cacheTime: evictCache ? null : 120000
        }
    };
};

export const fetchUserSocialProfile = (displayName = null, evictCache = false) => {
    return {
        type: types.FETCH_USER_SOCIAL_PROFILE,
        displayName,
        meta: {
            cacheTime: evictCache ? null : 120000
        }
    };
};

export const fetchUserSettings = (evictCache = false, callback = {}) => {
    return {
        type: types.FETCH_USER_SETTINGS,
        meta: {
            cacheTime: evictCache ? null : 120000
        },
        callback: callback
    };
};

export const fetchUserPrivacy = (displayName = null, evictCache = false) => {
    return {
        type: types.FETCH_PROFILE_USER_PRIVACY,
        displayName,
        meta: {
            cacheTime: evictCache ? null : 120000
        }
    };
};

export const fetchTimeZones = (evictCache = false) => {
    return {
        type: types.FETCH_TIMEZONES,
        meta: {
            cacheTime: evictCache ? null : 120000
        }
    };
};

export const fetchCurrentUserMinAge = () => {
    const url = `/currentuser-service/minAge`; // This end point is from modern, not GC-API

    return axios.get(url, { baseURL: '/modern' });
};

export const fetchCurrentUserInfo = () => {
    return {
        type: types.FETCH_CURRENT_USER_INFO
    };
};

export const fetchThirdPartyApps = () => {
    return {
        type: types.FETCH_THIRD_PARTY_APPS
    };
};

export const fetchThirdPartyAppsPermissions = () => {
    return {
        type: types.FETCH_THIRD_PARTY_APPS_PERMISSIONS
    };
};

export const fetchThirdPartyAppsCapabilities = (consumerKey = null) => {
    return {
        type: types.FETCH_THIRD_PARTY_APPS_CAPABILITIES,
        consumerKey: consumerKey
    };
};

export const fetchThirdPartyAppsAndCapabilities = () => {
    return {
        type: types.FETCH_THIRD_PARTY_APPS_AND_CAPABILITIES
    };
};

export function fetchOptionalFeature(featureType) {
    return {
        type: types.FETCH_OPTIONAL_FEATURE,
        featureType: featureType
    };
}

// Updates
export const updateViewerUserPreferences = (newViewerUserPreferences = {}, callback = {}) => {
    return {
        type: types.UPDATE_VIEWER_USER_PREFERENCES,
        newViewerUserPreferences,
        callback
    };
};

export const updateUserSocialProfile = (displayName = null, newUserSocialProfile = {}, callback = {}) => {
    return {
        type: types.UPDATE_USER_SOCIAL_PROFILE,
        displayName,
        newUserSocialProfile,
        callback
    };
};

export const updateUserPrivacy = (displayName = null, data = {}, privacy = {}) => {
    return {
        type: types.UPDATE_USER_SOCIAL_PRIVACY,
        displayName,
        data,
        privacy
    };
};

export const updateUserSettings = (newUserSettings = {}) => {
    return {
        type: types.UPDATE_USER_SETTINGS,
        payload: newUserSettings
    };
};

export const updateMctData = (newMctData = {}) => {
    return {
        type: types.UPDATE_USER_SETTINGS_MCT,
        payload: newMctData
    };
};

export const updateHydrationData = (newHydrationData = {}, calendarDate = null, callback = {}) => {
    return {
        type: types.UPDATE_HYDRATION_ALL_DATA,
        payload: {
            ...newHydrationData,
            calendarDate: calendarDate
        },
        callback
    };
};

export const uploadPublicProfileImage = (displayName = null, resizedImages = {}, callback = null) => {
    const url = `/image-service/imageservice/upload/${displayName}`;
    const formData = new FormData();

    if (resizedImages.large && resizedImages.medium && resizedImages.small) {
        formData.append('profileFile', resizedImages.large, 'fileName.png');
        formData.append('friendsFile', resizedImages.medium, 'fileName2.png');
        formData.append('thumbnailFile', resizedImages.small, 'fileName3.png');

        return gcApi
            .post(url, formData)
            .then((response) => {
                if (typeof callback === 'function') {
                    callback(response);
                }
            })
            .catch((error) => {
                if (typeof callback === 'function') {
                    callback(error, true);
                }
            });
    }
};

/**
 * update user public profile custom image/avatar/frame
 * @param {Object} payload
 * @param {string} payload.displayName user displayName
 * @param {{ large: Blob; medium: Blob; small: Blob }} [payload.resizedImages] image blobs. if existed, `payload.avatarUuid` will be ignore
 * @param {string} [payload.avatarUuid] user avatarUuid
 * @param {string} [payload.frameUuid] user frameUuid
 * @param {boolean} [payload.hasSubscribed] user has subscription
 * @param {(error: Error | null, response?: import('axios').AxiosResponse) => any} [payload.callback] done callback, use for updating view
 * @returns
 */
export const updateViewerPublicProfileGcPlus = (payload) => {
    return {
        type: types.UPDATE_VIEWER_PUBLIC_PROFILE_GC_PLUS,
        payload
    };
};

export const updateReduxUserPublicProfileImages = (displayName, imageUrls) => {
    return {
        type: types.UPDATE_REDUX_USER_PUBLIC_PROFILE_IMAGE,
        displayName,
        imageUrls
    };
};

export function updateThirdPartyAppPermission(appPermissions) {
    return {
        type: types.UPDATE_THIRD_PARTY_APPS_PERMISSIONS,
        appPermissions: appPermissions
    };
}

export function updateOptionalFeature(featureType, optionType, displayName, callback = {}) {
    return {
        type: types.UPDATE_OPTIONAL_FEATURE,
        featureType: featureType,
        optionType: optionType,
        displayName: displayName,
        callback: callback
    };
}

export const updateUserBadgePrivacy = (displayName = null, badgeVisibility = null, callback = null) => {
    return gcApi
        .put(`/userprofile-service/socialProfile/privacy/badge/${displayName}`, {
            badgeVisibility: badgeVisibility
        })
        .then(() => {
            if (typeof callback === 'function') {
                callback();
            }
        })
        .catch((error) => {
            if (typeof callback === 'function') {
                callback(error, true);
            }
        });
};

// Deletes
export const deletePublicProfileImage = (displayName = null, callback = null) => {
    return gcApi
        .delete(`/image-service/imageservice/remove/${displayName}`)
        .then(() => {
            if (typeof callback === 'function') {
                callback();
            }
        })
        .catch((error) => {
            if (typeof callback === 'function') {
                callback(error, true);
            }
        });
};

export function removeThirdPartyApp(consumerKey) {
    return {
        type: types.REMOVE_THIRD_PARTY_APP,
        consumerKey: consumerKey
    };
}

export function fetchDeviceBackupsSummary() {
    return {
        type: types.FETCH_DEVICE_BACKUPS_SUMMARY
    };
}
